import React, { useState, useEffect } from 'react';
import {
  X, CalendarClock, Truck, ClipboardList, AlertCircle, Info, CheckCircle,
  Clock, AlertTriangle, ChevronRight, ArrowLeft, ArrowRight
} from 'lucide-react';
import { useAuth } from '../../../../context/AuthContext';
import { useTimezone } from '../../../../hooks/useTimezone';
import { getCurrentUserId } from '../../../../utils/authUtils';
import { handleApiError } from '../../../../utils/errorUtils';
import truckService from './services/truckService';
import truckStatusService from '../../../../services/truckStatusService';
import StatusChangeConfirmation from './StatusChangeConfirmation';
import {
  isBackwardMovement,
  determineCurrentStatus,
  standardizeStatusChange,
  getAffectedTimestamps,
  getStatusSequence
} from './utils/statusUtils';
import { getDatePart, getTimePart, combineDateAndTime, zonedTimeToUtc } from './utils/dateUtils';
import {
  REF_LIST_MAX_LENGTH,
  INBOUND_PO_MAX_LENGTH,
  REF_LIST_HELPER_TEXT
} from './utils/refListUtils';
import TruckNotesThread from './TruckNotesThread';

/**
 * Redesigned truck editor with simplified UX and improved data handling
 */
const NewTruckEditor = ({
  onClose,
  onSave,
  truckData,
  mode = 'inbound',
  title
}) => {
  // Core state
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});
  const [activeStep, setActiveStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [businessUnits, setBusinessUnits] = useState([]);
  const [loadingBusinessUnits, setLoadingBusinessUnits] = useState(true);
  const [userNames, setUserNames] = useState({});

  // Status management
  const [showStatusConfirmation, setShowStatusConfirmation] = useState(false);
  const [statusChangeDetails, setStatusChangeDetails] = useState(null);

  // Check if there's a status change request in the truck data (for BOL uploads)
  useEffect(() => {
    if (truckData && truckData.__statusChangeRequest) {
      // Automatically trigger status change confirmation for BOL upload
      console.log("NewTruckEditor: Found status change request in truck data", truckData.__statusChangeRequest);
      setStatusChangeDetails(truckData.__statusChangeRequest);
      setShowStatusConfirmation(true);
    }
  }, [truckData]);
  
  // Auth and timezone context
  const { currentUser } = useAuth();
  const userTimezone = useTimezone();
  
  // Determine if creating new truck or editing existing
  const isNewTruck = !truckData || !truckData.id;
  
  // Define load types based on mode
  const loadTypes = mode === 'inbound'
    ? ['Live Unload', 'Container', 'Pallets', 'Parcel']
    : ['Live Unload', 'Container', 'Pallets', 'Parcel'];

  // Define status options based on mode - do not include 'Cancelled' in regular progression
  const statusOptions = mode === 'inbound'
    ? ['Scheduled', 'Arrived', 'Received', 'Putaway', 'Completed']
    : ['Scheduled', 'Staged', 'Loaded', 'Shipped', 'Completed'];

  // Handle 'Cancelled' status separately with its own button

  // Define timestamp fields for each status
  const statusTimestampFields = mode === 'inbound'
    ? {
        'Scheduled': 'scheduled_at',
        'Arrived': 'arrived_at',
        'Received': 'received_at',
        'Putaway': 'putaway_at',
        'Completed': 'completed_at',
        'Cancelled': 'cancelled_at'  // Add cancelled status
      }
    : {
        'Scheduled': 'scheduled_at',
        'Staged': 'staged_at',
        'Loaded': 'loaded_at',
        'Shipped': 'shipped_at',
        'Completed': 'completed_at',
        'Cancelled': 'cancelled_at'  // Add cancelled status
      };
  
  // Initialize form data
  useEffect(() => {
    // Format dates for form use
    let initialData = {...(truckData || {})};
    
    // Format the scheduled date into a local datetime-local input format
    // Convert UTC to user's timezone for proper display in form inputs
    if (initialData.scheduled_at) {
      try {
        const datePart = getDatePart(initialData.scheduled_at, userTimezone);
        const timePart = getTimePart(initialData.scheduled_at, userTimezone);
        if (datePart && timePart) {
          initialData.scheduled_at = `${datePart}T${timePart}`;
        }
      } catch (error) {
        console.error('Error formatting scheduled date:', error);
      }
    }
    
    // Ensure size is a number
    if (initialData.size) {
      initialData.size = Number(initialData.size);
    }
    
    // Set conform flag to boolean
    if (initialData.conforming !== undefined) {
      initialData.conforming = Boolean(initialData.conforming);
    }
    
    // Set secure flag to boolean
    if (initialData.secure !== undefined) {
      initialData.secure = Boolean(initialData.secure);
    }
    
    // Initialize new if empty
    if (isNewTruck) {
      initialData = {
        ...initialData,
        mode: mode,
        status: 'Scheduled',
        size: 1,
        secure: false,
        conforming: true
      };
    }
    
    setFormData(initialData);
  }, [truckData, mode, isNewTruck]);
  
  // Load business units on mount
  useEffect(() => {
    const fetchBusinessUnits = async () => {
      try {
        setLoadingBusinessUnits(true);
        const response = await truckService.getAll('business-units');
        
        // Format business units consistently
        if (Array.isArray(response)) {
          const formattedUnits = response.map(unit => ({
            id: unit.id || unit.business_unit_id || unit.value || unit,
            name: unit.name || unit.business_unit_name || unit.label || unit
          }));
          setBusinessUnits(formattedUnits);
        } else if (response && response.data && Array.isArray(response.data)) {
          const formattedUnits = response.data.map(unit => ({
            id: unit.id || unit.business_unit_id || unit.value || unit,
            name: unit.name || unit.business_unit_name || unit.label || unit
          }));
          setBusinessUnits(formattedUnits);
        }
      } catch (error) {
        console.error('Error fetching business units:', error);
        // Fallback data
        setBusinessUnits([
          { id: '1', name: 'Electronics' },
          { id: '2', name: 'Furniture' },
          { id: '3', name: 'Apparel' },
          { id: '4', name: 'Automotive' }
        ]);
      } finally {
        setLoadingBusinessUnits(false);
      }
    };
    
    fetchBusinessUnits();
  }, []);
  
  // Fetch user names for created_by and updated_by fields
  const fetchUserNames = async (userIds) => {
    if (!userIds || userIds.length === 0) return;
    
    try {
      const API_URL = process.env.REACT_APP_API_URL || '/api';
      const token = localStorage.getItem('token');
      
      // Filter out IDs we already have cached
      const uncachedIds = userIds.filter(id => id && !userNames[id]);
      
      if (uncachedIds.length === 0) return;
      
      // Fetch user details for uncached IDs
      const userPromises = uncachedIds.map(async (userId) => {
        try {
          const response = await fetch(`${API_URL}/users/${userId}`, {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });
          
          if (response.ok) {
            const user = await response.json();
            return {
              id: userId,
              name: user.full_name || user.username || `User ${userId}`
            };
          }
        } catch (error) {
          console.warn(`Failed to fetch user ${userId}:`, error);
        }
        
        return {
          id: userId,
          name: `User ${userId}` // Fallback
        };
      });
      
      const userResults = await Promise.all(userPromises);
      
      // Update userNames state with new mappings
      const newUserNames = {};
      userResults.forEach(result => {
        if (result) {
          newUserNames[result.id] = result.name;
        }
      });
      
      setUserNames(prev => ({
        ...prev,
        ...newUserNames
      }));
      
    } catch (error) {
      console.error('Error fetching user names:', error);
    }
  };
  
  // Load user names when formData changes
  useEffect(() => {
    if (!isNewTruck && formData) {
      const userIdsToFetch = [];
      
      if (formData.created_by) {
        userIdsToFetch.push(formData.created_by);
      }
      
      if (formData.updated_by && formData.updated_by !== formData.created_by) {
        userIdsToFetch.push(formData.updated_by);
      }
      
      if (userIdsToFetch.length > 0) {
        fetchUserNames(userIdsToFetch);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.created_by, formData.updated_by, isNewTruck]);
  
  // Handle input changes
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    // Handle different field types
    let newValue = type === 'checkbox' ? checked : value;
    
    // Special handling for numeric fields
    if (type === 'number') {
      newValue = value === '' ? '' : Number(value);
    }
    
    // Update form data
    setFormData(prev => ({
      ...prev,
      [name]: newValue
    }));
    
    // Clear error when field is changed
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };
  
  // Validate the form
  const validateForm = () => {
    const newErrors = {};
    
    // Required fields
    if (!formData.business_unit) newErrors.business_unit = 'Business unit is required';
    if (!formData.carrier) newErrors.carrier = 'Carrier is required';
    if (!formData.load_type) newErrors.load_type = 'Load type is required';
    if (!formData.size) newErrors.size = 'Size is required';
    if (!formData.scheduled_at) newErrors.scheduled_at = 'Scheduled date is required';
    
    // Mode-specific validations
    if (mode === 'outbound') {
      if (!formData.po) newErrors.po = 'PO number is required for outbound trucks';
      if (!formData.customer) newErrors.customer = 'Customer is required for outbound trucks';
    }
    
    // Date validation
    if (formData.scheduled_at) {
      const scheduledDate = new Date(formData.scheduled_at);
      if (isNaN(scheduledDate.getTime())) {
        newErrors.scheduled_at = 'Invalid date format';
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  // Handle form submission
  const handleSubmit = async (e) => {
    if (e) {
      e.preventDefault();
    }

    // Avoid duplicate submissions
    if (isSubmitting) return;

    // Set submission flag
    setIsSubmitting(true);

    try {
      // Validate the form
      if (!validateForm()) {
        setIsSubmitting(false);
        return false;
      }

      // Get user ID for tracking
      const userId = getCurrentUserId();

      // Prepare truck data for API
      const updatedTruck = {
        ...formData,
        // Set creator for new trucks
        ...(isNewTruck ? { created_by: userId } : {}),
        updated_by: userId
      };

      // Convert scheduled_at from CST datetime-local input to UTC for database storage
      if (updatedTruck.scheduled_at) {
        try {
          // The datetime-local input gives us a string like "2024-01-15T14:30"
          // We need to treat this as CST time and convert to UTC
          const localDateTime = new Date(updatedTruck.scheduled_at);
          const utcDateTime = zonedTimeToUtc(localDateTime, userTimezone);
          updatedTruck.scheduled_at = utcDateTime.toISOString();
        } catch (error) {
          console.error('Error converting scheduled_at to UTC:', error);
          // If conversion fails, keep the original value and let server handle it
        }
      }

      // Check if this is a status change and if it's backward
      if (!isNewTruck && truckData && truckData.status !== formData.status) {
        const originalStatus = truckData.status;
        const newStatus = formData.status;

        const isMovingBackward = isBackwardMovement(truckData, newStatus, mode);
        console.log(`NewTruckEditor: Status change detected from ${originalStatus} to ${newStatus} (backward: ${isMovingBackward})`);

        if (isMovingBackward) {
          try {
            // Use a direct API call instead of the status service
            console.log(`NewTruckEditor: Using direct API call for backward status change`);

            const token = localStorage.getItem('token');
            const API_URL = process.env.REACT_APP_API_URL || '/api';
            const endpoint = `${API_URL}/${mode === 'inbound' ? 'inbound-trucks' : 'outbound-trucks'}/${truckData.id}`;

            // Create update data with nullified timestamps
            // Specifically for moving backward, we need to explicitly clear timestamps
            const updateData = {
              status: newStatus
            };

            // Clear timestamp fields for all statuses after the target status
            const timestampsToClear = getAffectedTimestamps(originalStatus, newStatus, mode);
            timestampsToClear.forEach(field => {
              updateData[field] = null;
            });

            console.log('Direct API call with data:', updateData);

            const response = await fetch(endpoint, {
              method: 'PUT',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
              },
              body: JSON.stringify(updateData)
            });

            if (!response.ok) {
              const errorData = await response.json();
              throw new Error(errorData.message || `Server returned ${response.status}`);
            }

            const result = await response.json();

            console.log('NewTruckEditor: Backward status movement success', result);

            // Close the modal on success
            onClose();
            return true;
          } catch (error) {
            console.error('Status change error:', error);

            setErrors(prev => ({
              ...prev,
              general: `Error changing status: ${error.message}`
            }));

            setIsSubmitting(false);
            return false;
          }
        }
      }

      // For new trucks or non-backward status changes, use the normal save method
      console.log('NewTruckEditor: Using standard truck update');
      const result = await onSave(updatedTruck);

      if (result === true || (result && !result.errors)) {
        // Success - close the modal
        onClose();
        return true;
      } else if (result && typeof result === 'object' && result.errors) {
        // Handle validation errors from the server
        setErrors(prev => ({
          ...prev,
          ...result.errors,
          general: 'Please correct the errors below'
        }));
        return false;
      } else {
        // Generic error
        setErrors(prev => ({
          ...prev,
          general: 'The server could not process your request. Please try again.'
        }));
        return false;
      }
    } catch (error) {
      console.error('Form submission error:', error);
      const errorResult = handleApiError(error);

      setErrors(prev => ({
        ...prev,
        general: errorResult.message || 'An unexpected error occurred during submission'
      }));
      return false;
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // Format a date for display
  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      return date.toLocaleString();
    } catch (error) {
      return dateString;
    }
  };
  
  // Determine loading state
  const isLoading = isSubmitting || loadingBusinessUnits;

  // Handle status change request
  const handleStatusChange = (newStatus) => {
    // Validate that we can change to this status
    // Allow 'Cancelled' as a special case
    if (!newStatus || (!statusOptions.includes(newStatus) && newStatus !== 'Cancelled')) {
      console.error('Invalid status:', newStatus);
      return;
    }

    // Use statusUtils to determine the current status and if it's a backward movement
    const currentStatus = determineCurrentStatus(formData, mode);

    // Special handling for Cancelled status
    const isMovingBackward = newStatus === 'Cancelled' ?
      false : isBackwardMovement(formData, newStatus, mode);

    console.log(`NewTruckEditor: Status change requested from ${currentStatus} to ${newStatus}. Backward: ${isMovingBackward}`);

    // Get affected fields for display
    let affectedFields = [];
    if (isMovingBackward && newStatus !== 'Cancelled') {
      // Get all timestamp fields that would be cleared
      const statusSequence = statusOptions;
      const currentIndex = statusSequence.indexOf(currentStatus);
      const newIndex = statusSequence.indexOf(newStatus);

      affectedFields = statusSequence
        .slice(newIndex + 1, currentIndex + 1)
        .map(s => statusTimestampFields[s]);

      console.log('Fields that will be nullified:', affectedFields);
    }

    // Get status sequence to use for properly formatted status objects
    const statusSequence = getStatusSequence(mode);
    const fromStatusObj = statusSequence.find(s => s.key === currentStatus) ||
      { key: currentStatus, label: currentStatus, timestampField: statusTimestampFields[currentStatus] };
    const toStatusObj = statusSequence.find(s => s.key === newStatus) ||
      { key: newStatus, label: newStatus, timestampField: statusTimestampFields[newStatus] };

    // Create details for confirmation with proper status objects
    setStatusChangeDetails({
      currentStatus: currentStatus,
      newStatus: newStatus,
      isBackward: isMovingBackward,
      affectedFields: affectedFields,
      fromStatus: fromStatusObj,
      toStatus: toStatusObj,
      direction: isMovingBackward ? 'backward' : 'forward'
    });

    // Show confirmation dialog
    setShowStatusConfirmation(true);
  };

  // Confirm status change
  const confirmStatusChange = () => {
    if (!statusChangeDetails) return;

    const { newStatus, isBackward } = statusChangeDetails;
    const currentStatus = determineCurrentStatus(formData, mode);

    console.log(`NewTruckEditor: Confirming status change from ${currentStatus} to ${newStatus} (backward: ${isBackward})`);

    // Handle cancellations specially
    if (newStatus === 'Cancelled') {
      // Create a copy of form data with cancellation info
      // Use current time in UTC for cancellation timestamp
      const cancelDate = new Date(); // This is already in UTC
      const formattedCancelDate = cancelDate.toISOString().slice(0, 19).replace('T', ' ');

      const updatedData = {
        ...formData,
        status: 'Cancelled',
        cancelled_at: formattedCancelDate // Set UTC timestamp as cancellation time
      };

      console.log('NewTruckEditor: Cancellation applied with data:', updatedData);
      setFormData(updatedData);
    } else {
      // Normal status change using the standardized function
      const updatedData = standardizeStatusChange(
        formData,
        newStatus,
        mode,
        isBackward
      );

      console.log('NewTruckEditor: Status change applied with standardized data:', updatedData);
      setFormData(updatedData);
    }

    // Close confirmation dialog
    setShowStatusConfirmation(false);
    setStatusChangeDetails(null);
  };

  // Cancel status change
  const cancelStatusChange = () => {
    setShowStatusConfirmation(false);
    setStatusChangeDetails(null);
  };
  
  // Get current business unit name for display
  const getCurrentBusinessUnitName = () => {
    if (!formData.business_unit) return '';
    const unit = businessUnits.find(unit => unit.id.toString() === formData.business_unit.toString());
    return unit ? unit.name : formData.business_unit;
  };
  
  return (
    <div className="fixed inset-0 bg-black/75 flex items-center justify-center p-4 z-50 overflow-y-auto" onClick={(e) => e.stopPropagation()}>
      <div 
        className="bg-slate-800 rounded-lg w-full max-w-3xl shadow-xl" 
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-700 flex items-center">
          <h2 className="text-xl font-medium text-white">
            {title || (isNewTruck 
              ? `Schedule New ${mode === 'inbound' ? 'Inbound' : 'Outbound'} Truck` 
              : `Edit ${mode === 'inbound' ? 'Inbound' : 'Outbound'} Truck`)}
          </h2>
          <div className="ml-auto">
            <button 
              className="p-1 text-slate-400 hover:text-white hover:bg-slate-700 rounded-full"
              onClick={onClose}
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
        
        {/* Progress Indicator */}
        <div className="px-6 pt-4 pb-2">
          <div className="flex justify-between">
            <div 
              className={`flex-1 text-center py-1 rounded-l-md ${activeStep === 1 ? 'bg-blue-600 text-white' : 'bg-slate-700 text-slate-400'}`}
              onClick={() => !isSubmitting && setActiveStep(1)}
            >
              <span className="text-xs font-medium">Basic Details</span>
            </div>
            <div 
              className={`flex-1 text-center py-1 ${activeStep === 2 ? 'bg-blue-600 text-white' : 'bg-slate-700 text-slate-400'}`}
              onClick={() => !isSubmitting && setActiveStep(2)}
            >
              <span className="text-xs font-medium">Shipping Details</span>
            </div>
            <div
              className={`flex-1 text-center py-1 ${activeStep === 3 ? 'bg-blue-600 text-white' : 'bg-slate-700 text-slate-400'}`}
              onClick={() => !isSubmitting && setActiveStep(3)}
            >
              <span className="text-xs font-medium">Additional Details</span>
            </div>
            <div
              className={`flex-1 text-center py-1 rounded-r-md ${activeStep === 4 ? 'bg-blue-600 text-white' : 'bg-slate-700 text-slate-400'}`}
              onClick={() => !isSubmitting && setActiveStep(4)}
            >
              <span className="text-xs font-medium">Status Management</span>
            </div>
          </div>
        </div>
        
        {/* Error messages */}
        {errors.general && (
          <div className="mx-6 mt-3 p-3 bg-red-900/30 border border-red-800 rounded-md">
            <div className="flex items-start">
              <AlertCircle className="w-5 h-5 text-red-500 mr-2 flex-shrink-0" />
              <span className="text-red-200 text-sm">{errors.general}</span>
            </div>
          </div>
        )}
        
        {/* Form Content */}
        <form onSubmit={handleSubmit} className="px-6 py-4" noValidate>
          {/* Step 1: Basic Details */}
          {activeStep === 1 && (
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-blue-400 mb-2">
                <Truck className="w-5 h-5" />
                <h3 className="text-lg font-medium">Basic Truck Information</h3>
              </div>
              
              {/* Business Unit */}
              <div className="form-group">
                <label htmlFor="business_unit" className="block text-sm font-medium text-slate-300 mb-1">
                  Business Unit <span className="text-red-500">*</span>
                </label>
                <select
                  id="business_unit"
                  name="business_unit"
                  value={formData.business_unit || ''}
                  onChange={handleChange}
                  className={`w-full bg-slate-700 border ${errors.business_unit ? 'border-red-500' : 'border-slate-600'} rounded-md px-3 py-2 text-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500`}
                  disabled={isLoading}
                  required
                >
                  <option value="">Select Business Unit</option>
                  {businessUnits.map(unit => (
                    <option key={unit.id} value={unit.id}>
                      {unit.name}
                    </option>
                  ))}
                </select>
                {errors.business_unit && (
                  <p className="mt-1 text-sm text-red-500">{errors.business_unit}</p>
                )}
              </div>
              
              {/* Carrier */}
              <div className="form-group">
                <label htmlFor="carrier" className="block text-sm font-medium text-slate-300 mb-1">
                  Carrier <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="carrier"
                  name="carrier"
                  value={formData.carrier || ''}
                  onChange={handleChange}
                  className={`w-full bg-slate-700 border ${errors.carrier ? 'border-red-500' : 'border-slate-600'} rounded-md px-3 py-2 text-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500`}
                  placeholder="Enter carrier name"
                  disabled={isLoading}
                  required
                />
                {errors.carrier && (
                  <p className="mt-1 text-sm text-red-500">{errors.carrier}</p>
                )}
              </div>
              
              {/* Schedule Date/Time */}
              <div className="form-group">
                <label htmlFor="scheduled_at" className="block text-sm font-medium text-slate-300 mb-1">
                  Scheduled Date & Time <span className="text-red-500">*</span> <span className="text-slate-400 text-xs">(CST)</span>
                </label>
                <div className="relative">
                  <CalendarClock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
                  <input
                    type="datetime-local"
                    id="scheduled_at"
                    name="scheduled_at"
                    value={formData.scheduled_at || ''}
                    onChange={handleChange}
                    className={`w-full bg-slate-700 border ${errors.scheduled_at ? 'border-red-500' : 'border-slate-600'} rounded-md px-3 py-2 pl-10 text-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500`}
                    disabled={isLoading}
                    required
                  />
                </div>
                {errors.scheduled_at && (
                  <p className="mt-1 text-sm text-red-500">{errors.scheduled_at}</p>
                )}
              </div>
              
              {/* Navigation buttons */}
              <div className="flex justify-end mt-6 pt-4 border-t border-slate-700">
                <button
                  type="button"
                  className="px-4 py-2 bg-slate-700 text-slate-300 rounded-md hover:bg-slate-600 mr-3"
                  onClick={onClose}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                  onClick={() => setActiveStep(2)}
                  disabled={isLoading}
                >
                  Continue
                </button>
              </div>
            </div>
          )}
          
          {/* Step 2: Shipping Details */}
          {activeStep === 2 && (
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-blue-400 mb-2">
                <ClipboardList className="w-5 h-5" />
                <h3 className="text-lg font-medium">Shipping Information</h3>
              </div>
              
              {/* Load Type */}
              <div className="form-group">
                <label htmlFor="load_type" className="block text-sm font-medium text-slate-300 mb-1">
                  Load Type <span className="text-red-500">*</span>
                </label>
                <select
                  id="load_type"
                  name="load_type"
                  value={formData.load_type || ''}
                  onChange={handleChange}
                  className={`w-full bg-slate-700 border ${errors.load_type ? 'border-red-500' : 'border-slate-600'} rounded-md px-3 py-2 text-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500`}
                  disabled={isLoading}
                  required
                >
                  <option value="">Select Load Type</option>
                  {loadTypes.map(type => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
                {errors.load_type && (
                  <p className="mt-1 text-sm text-red-500">{errors.load_type}</p>
                )}
              </div>
              
              {/* Size (Pallets) */}
              <div className="form-group">
                <label htmlFor="size" className="block text-sm font-medium text-slate-300 mb-1">
                  Size (Pallets) <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  id="size"
                  name="size"
                  value={formData.size || ''}
                  onChange={handleChange}
                  min="1"
                  max="100"
                  className={`w-full bg-slate-700 border ${errors.size ? 'border-red-500' : 'border-slate-600'} rounded-md px-3 py-2 text-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500`}
                  disabled={isLoading}
                  required
                />
                {errors.size && (
                  <p className="mt-1 text-sm text-red-500">{errors.size}</p>
                )}
              </div>
              
              {/* PO Number — on outbound this may hold a comma-separated list for multi-order
                  trucks, positionally paired with Order Number. maxLength mirrors the DB column,
                  which differs by mode (inbound po is the narrower VARCHAR(115)). */}
              <div className="form-group">
                <label htmlFor="po" className="block text-sm font-medium text-slate-300 mb-1">
                  PO Number {mode === 'outbound' && <span className="text-red-500">*</span>}
                </label>
                <input
                  type="text"
                  id="po"
                  name="po"
                  value={formData.po || ''}
                  onChange={handleChange}
                  maxLength={mode === 'outbound' ? REF_LIST_MAX_LENGTH : INBOUND_PO_MAX_LENGTH}
                  className={`w-full bg-slate-700 border ${errors.po ? 'border-red-500' : 'border-slate-600'} rounded-md px-3 py-2 text-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500`}
                  placeholder="Enter PO number"
                  disabled={isLoading}
                  required={mode === 'outbound'}
                />
                {errors.po ? (
                  <p className="mt-1 text-sm text-red-500">{errors.po}</p>
                ) : (
                  mode === 'outbound' && (
                    <p className="mt-1 text-xs text-slate-400">{REF_LIST_HELPER_TEXT}</p>
                  )
                )}
              </div>

              {/* Order Number */}
              <div className="form-group">
                <label htmlFor="order_number" className="block text-sm font-medium text-slate-300 mb-1">
                  Order Number
                </label>
                <input
                  type="text"
                  id="order_number"
                  name="order_number"
                  value={formData.order_number || ''}
                  onChange={handleChange}
                  maxLength={REF_LIST_MAX_LENGTH}
                  className={`w-full bg-slate-700 border ${errors.order_number ? 'border-red-500' : 'border-slate-600'} rounded-md px-3 py-2 text-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500`}
                  placeholder="Enter order number"
                  disabled={isLoading}
                />
                {errors.order_number ? (
                  <p className="mt-1 text-sm text-red-500">{errors.order_number}</p>
                ) : (
                  mode === 'outbound' && (
                    <p className="mt-1 text-xs text-slate-400">{REF_LIST_HELPER_TEXT}</p>
                  )
                )}
              </div>
              
              {/* Vendor (inbound only) */}
              {mode === 'inbound' && (
                <div className="form-group">
                  <label htmlFor="vendor" className="block text-sm font-medium text-slate-300 mb-1">
                    Vendor
                  </label>
                  <input
                    type="text"
                    id="vendor"
                    name="vendor"
                    value={formData.vendor || ''}
                    onChange={handleChange}
                    className={`w-full bg-slate-700 border ${errors.vendor ? 'border-red-500' : 'border-slate-600'} rounded-md px-3 py-2 text-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500`}
                    placeholder="Enter vendor name"
                    disabled={isLoading}
                  />
                  {errors.vendor && (
                    <p className="mt-1 text-sm text-red-500">{errors.vendor}</p>
                  )}
                </div>
              )}
              
              {/* Dock Door */}
              <div className="form-group">
                <label htmlFor="dock_door" className="block text-sm font-medium text-slate-300 mb-1">
                  Dock Door
                </label>
                <input
                  type="text"
                  id="dock_door"
                  name="dock_door"
                  value={formData.dock_door || ''}
                  onChange={handleChange}
                  className={`w-full bg-slate-700 border ${errors.dock_door ? 'border-red-500' : 'border-slate-600'} rounded-md px-3 py-2 text-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500`}
                  placeholder="e.g. D1, D2, etc."
                  disabled={isLoading}
                />
                {errors.dock_door && (
                  <p className="mt-1 text-sm text-red-500">{errors.dock_door}</p>
                )}
              </div>
              
              {/* Outbound specific fields */}
              {mode === 'outbound' && (
                <>
                  <div className="form-group">
                    <label htmlFor="customer" className="block text-sm font-medium text-slate-300 mb-1">
                      Customer <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="customer"
                      name="customer"
                      value={formData.customer || ''}
                      onChange={handleChange}
                      className={`w-full bg-slate-700 border ${errors.customer ? 'border-red-500' : 'border-slate-600'} rounded-md px-3 py-2 text-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500`}
                      placeholder="Enter customer name"
                      disabled={isLoading}
                      required
                    />
                    {errors.customer && (
                      <p className="mt-1 text-sm text-red-500">{errors.customer}</p>
                    )}
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="destination" className="block text-sm font-medium text-slate-300 mb-1">
                      Destination
                    </label>
                    <input
                      type="text"
                      id="destination"
                      name="destination"
                      value={formData.destination || ''}
                      onChange={handleChange}
                      className={`w-full bg-slate-700 border ${errors.destination ? 'border-red-500' : 'border-slate-600'} rounded-md px-3 py-2 text-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500`}
                      placeholder="Enter destination"
                      disabled={isLoading}
                    />
                  </div>
                </>
              )}
              
              {/* Navigation buttons */}
              <div className="flex justify-between mt-6 pt-4 border-t border-slate-700">
                <button
                  type="button"
                  className="px-4 py-2 bg-slate-700 text-slate-300 rounded-md hover:bg-slate-600"
                  onClick={() => setActiveStep(1)}
                  disabled={isLoading}
                >
                  Back
                </button>
                <button
                  type="button"
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                  onClick={() => setActiveStep(3)}
                  disabled={isLoading}
                >
                  Continue
                </button>
              </div>
            </div>
          )}
          
          {/* Step 3: Additional Details */}
          {activeStep === 3 && (
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-blue-400 mb-2">
                <Info className="w-5 h-5" />
                <h3 className="text-lg font-medium">Additional Details</h3>
              </div>
              
              {/* Secure Shipment */}
              <div className="form-group">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="secure"
                    name="secure"
                    checked={formData.secure || false}
                    onChange={handleChange}
                    className="w-4 h-4 text-blue-600 bg-slate-700 border-slate-600 rounded focus:ring-blue-500"
                    disabled={isLoading}
                  />
                  <label htmlFor="secure" className="ml-2 text-sm text-slate-300">
                    Secure Shipment (requires additional security measures)
                  </label>
                </div>
              </div>
              
              {/* Notes thread (multi-note: any user, any stage, auto-tagged) */}
              {!isNewTruck && truckData?.id && (
                <div className="form-group">
                  <label className="block text-sm font-medium text-slate-300 mb-1">
                    Notes
                  </label>
                  <TruckNotesThread
                    truckType={mode}
                    truckId={truckData.id}
                    mode="edit"
                    theme="dark"
                    emptyMessage="No notes yet — add one below."
                  />
                </div>
              )}
              {isNewTruck && (
                <div className="form-group">
                  <p className="text-xs text-slate-400 italic">
                    You can add notes after saving this truck.
                  </p>
                </div>
              )}

              {/* Mode-specific fields */}
              {mode === 'inbound' ? (
                <>
                  {/* Show if truck is already received */}
                  {['Received', 'Putaway', 'Completed'].includes(formData.status) && (
                    <>
                      <div className="form-group">
                        <div className="flex items-center">
                          <input
                            type="checkbox"
                            id="conforming"
                            name="conforming"
                            checked={formData.conforming || false}
                            onChange={handleChange}
                            className="w-4 h-4 text-blue-600 bg-slate-700 border-slate-600 rounded focus:ring-blue-500"
                            disabled={isLoading}
                          />
                          <label htmlFor="conforming" className="ml-2 text-sm text-slate-300">
                            Shipment is conforming to requirements
                          </label>
                        </div>
                      </div>
                    </>
                  )}
                </>
              ) : (
                <>
                  <div className="form-group">
                    <label htmlFor="staging_area" className="block text-sm font-medium text-slate-300 mb-1">
                      Staging Area
                    </label>
                    <input
                      type="text"
                      id="staging_area"
                      name="staging_area"
                      value={formData.staging_area || ''}
                      onChange={handleChange}
                      className={`w-full bg-slate-700 border ${errors.staging_area ? 'border-red-500' : 'border-slate-600'} rounded-md px-3 py-2 text-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500`}
                      placeholder="Enter staging area location"
                      disabled={isLoading}
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="priority" className="block text-sm font-medium text-slate-300 mb-1">
                      Priority
                    </label>
                    <select
                      id="priority"
                      name="priority"
                      value={formData.priority || 'Normal'}
                      onChange={handleChange}
                      className={`w-full bg-slate-700 border ${errors.priority ? 'border-red-500' : 'border-slate-600'} rounded-md px-3 py-2 text-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500`}
                      disabled={isLoading}
                    >
                      <option value="High">High</option>
                      <option value="Normal">Normal</option>
                      <option value="Low">Low</option>
                    </select>
                  </div>
                </>
              )}
              
              {/* Summary Section */}
              <div className="mt-6 p-3 rounded-md bg-slate-750 border border-slate-600">
                <h4 className="text-sm font-medium text-slate-300 mb-2 flex items-center">
                  <CheckCircle className="w-4 h-4 mr-1 text-green-500" />
                  Summary
                </h4>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div><span className="text-slate-400">Business Unit:</span> <span className="text-slate-200">{getCurrentBusinessUnitName()}</span></div>
                  <div><span className="text-slate-400">Carrier:</span> <span className="text-slate-200">{formData.carrier || ''}</span></div>
                  <div><span className="text-slate-400">Scheduled:</span> <span className="text-slate-200">{formatDate(formData.scheduled_at)}</span></div>
                  <div><span className="text-slate-400">Load Type:</span> <span className="text-slate-200">{formData.load_type || ''}</span></div>
                </div>
              </div>
              
              {/* Metadata for existing trucks */}
              {!isNewTruck && (
                <div className="mt-4 pt-4 border-t border-slate-700">
                  <div className="grid grid-cols-2 gap-2 text-xs text-slate-400">
                    <div>Created By: <span className="text-slate-300">{userNames[formData.created_by] || formData.created_by || '-'}</span></div>
                    <div>Created: <span className="text-slate-300">{formatDate(formData.created_at)}</span></div>
                    <div>Updated By: <span className="text-slate-300">{userNames[formData.updated_by] || formData.updated_by || '-'}</span></div>
                    <div>Updated: <span className="text-slate-300">{formatDate(formData.updated_at)}</span></div>
                  </div>
                </div>
              )}
              
              {/* Navigation buttons */}
              <div className="flex justify-between mt-6 pt-4 border-t border-slate-700">
                <button
                  type="button"
                  className="px-4 py-2 bg-slate-700 text-slate-300 rounded-md hover:bg-slate-600"
                  onClick={() => setActiveStep(2)}
                  disabled={isLoading}
                >
                  Back
                </button>
                <div>
                  <button
                    type="button"
                    className="px-4 py-2 bg-slate-700 text-slate-300 rounded-md hover:bg-slate-600 mr-3"
                    onClick={onClose}
                    disabled={isLoading}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 mr-3"
                    onClick={() => setActiveStep(4)}
                    disabled={isLoading}
                  >
                    Next: Status Management
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Step 4: Status Management */}
          {activeStep === 4 && (
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-blue-400 mb-2">
                <Clock className="w-5 h-5" />
                <h3 className="text-lg font-medium">Status Management</h3>
              </div>

              {/* Current Status */}
              <div className="p-4 bg-slate-750 rounded-md border border-slate-600 mb-4">
                <div className="flex justify-between items-center mb-3">
                  <h4 className="text-slate-300 font-medium">Current Status</h4>
                  <div className="px-3 py-1 bg-blue-600 text-white text-sm rounded-full">
                    {formData.status || 'Scheduled'}
                  </div>
                </div>

                {/* Status Timeline */}
                <div className="mt-4 flex items-center justify-between">
                  {statusOptions.map((status, index) => {
                    const isCurrentStatus = status === (formData.status || 'Scheduled');
                    const isPastStatus = statusOptions.indexOf(formData.status || 'Scheduled') >= index;

                    return (
                      <div key={status} className="flex flex-col items-center space-y-1 relative">
                        <div
                          className={`w-8 h-8 rounded-full flex items-center justify-center
                            ${isCurrentStatus
                              ? 'bg-blue-600 text-white ring-2 ring-blue-400 ring-opacity-50'
                              : isPastStatus
                                ? 'bg-green-600 text-white'
                                : 'bg-slate-700 text-slate-400'
                            }`}
                        >
                          {index + 1}
                        </div>
                        <span className={`text-xs ${isCurrentStatus ? 'text-blue-400 font-medium' : 'text-slate-400'}`}>
                          {status}
                        </span>

                        {/* Connector line */}
                        {index < statusOptions.length - 1 && (
                          <div className="absolute top-4 left-8 w-[calc(100%-16px)] h-0.5 bg-slate-700 -z-10">
                            {isPastStatus && statusOptions.indexOf(formData.status || 'Scheduled') > index && (
                              <div className="absolute top-0 left-0 h-full bg-green-600" style={{ width: '100%' }}></div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* Timestamps */}
                <div className="mt-6 grid grid-cols-2 gap-3 text-sm">
                  {statusOptions.map(status => {
                    const timestampField = statusTimestampFields[status];
                    const timestamp = formData[timestampField];

                    return (
                      <div key={status} className="flex justify-between">
                        <span className="text-slate-400">{status}:</span>
                        <span className="text-slate-300">
                          {timestamp ? formatDate(timestamp) : 'Not set'}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Change Status */}
              <div className="p-4 bg-slate-750 rounded-md border border-slate-600">
                <h4 className="text-slate-300 font-medium mb-3">Change Status</h4>

                <p className="text-sm text-slate-400 mb-4">
                  Select a new status for this truck. Changing to a later status will add the current timestamp.
                  Changing to an earlier status will remove timestamps for all subsequent statuses.
                </p>

                {/* Status Selection */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
                  {statusOptions.map(status => {
                    const isCurrentStatus = status === (formData.status || 'Scheduled');
                    const isPastStatus = statusOptions.indexOf(formData.status || 'Scheduled') >= statusOptions.indexOf(status);
                    const isFutureStatus = statusOptions.indexOf(formData.status || 'Scheduled') < statusOptions.indexOf(status);

                    return (
                      <button
                        key={status}
                        type="button"
                        disabled={isCurrentStatus || isLoading}
                        className={`p-3 rounded-md border flex items-center justify-between
                          ${isCurrentStatus
                            ? 'bg-blue-900/30 border-blue-800 text-blue-300 cursor-default'
                            : isPastStatus
                              ? 'bg-amber-900/20 border-amber-900/30 text-amber-200 hover:bg-amber-900/30'
                              : 'bg-green-900/20 border-green-900/30 text-green-200 hover:bg-green-900/30'
                          }`}
                        onClick={() => handleStatusChange(status)}
                      >
                        <span className="font-medium">{status}</span>

                        {isPastStatus && !isCurrentStatus ? (
                          <ArrowLeft className="w-5 h-5 text-amber-400" />
                        ) : isFutureStatus ? (
                          <ArrowRight className="w-5 h-5 text-green-400" />
                        ) : (
                          <CheckCircle className="w-5 h-5 text-blue-400" />
                        )}
                      </button>
                    );
                  })}

                  {/* Cancel Button - always available unless status is already Cancelled */}
                  {formData.status !== 'Cancelled' && (
                    <button
                      type="button"
                      className="p-3 rounded-md border bg-red-900/20 border-red-900/30 text-red-200 hover:bg-red-900/30 flex items-center justify-between col-span-full"
                      onClick={() => handleStatusChange('Cancelled')}
                    >
                      <span className="font-medium">Cancel Truck</span>
                      <X className="w-5 h-5 text-red-400" />
                    </button>
                  )}
                </div>

                {!isNewTruck && (
                  <div className="text-xs text-amber-300 flex items-center mt-6 mb-2">
                    <AlertTriangle className="w-4 h-4 mr-1 flex-shrink-0" />
                    <span>
                      Changing the status will update timestamps and might affect associated workflows.
                      Use with caution.
                    </span>
                  </div>
                )}
              </div>

              {/* Navigation buttons */}
              <div className="flex justify-between mt-6 pt-4 border-t border-slate-700">
                <button
                  type="button"
                  className="px-4 py-2 bg-slate-700 text-slate-300 rounded-md hover:bg-slate-600"
                  onClick={() => setActiveStep(3)}
                  disabled={isLoading}
                >
                  Back
                </button>
                <div>
                  <button
                    type="button"
                    className="px-4 py-2 bg-slate-700 text-slate-300 rounded-md hover:bg-slate-600 mr-3"
                    onClick={onClose}
                    disabled={isLoading}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className={`px-4 py-2 rounded-md ${mode === 'inbound' ? 'bg-blue-600 hover:bg-blue-700' : 'bg-green-600 hover:bg-green-700'} text-white ${isLoading ? 'opacity-60 cursor-not-allowed' : ''}`}
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <div className="flex items-center">
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Processing...
                      </div>
                    ) : (
                      isNewTruck ? 'Schedule Truck' : 'Save Changes'
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}
        </form>
      </div>

      {/* Status Change Confirmation Modal */}
      {showStatusConfirmation && statusChangeDetails && (
        <div className="fixed inset-0 bg-black/75 flex items-center justify-center z-50">
          <div className="bg-slate-800 rounded-lg p-5 max-w-md w-full mx-4">
            <h3 className="text-lg font-medium text-white mb-3">
              {statusChangeDetails.newStatus === 'Cancelled' ? 'Confirm Cancellation' : 'Confirm Status Change'}
            </h3>

            {statusChangeDetails.newStatus === 'Cancelled' ? (
              <p className="text-slate-300 mb-4">
                Are you sure you want to <span className="font-medium text-red-400">cancel</span> this truck?
                This action will mark the truck as cancelled and record the current time as the cancellation timestamp.
              </p>
            ) : (
              <p className="text-slate-300 mb-4">
                Are you sure you want to change the status from{' '}
                <span className="font-medium text-blue-400">{statusChangeDetails.currentStatus}</span> to{' '}
                <span className="font-medium text-green-400">{statusChangeDetails.newStatus}</span>?
              </p>
            )}

            {statusChangeDetails.newStatus !== 'Cancelled' && statusChangeDetails.isBackward && statusChangeDetails.affectedFields.length > 0 && (
              <div className="p-3 bg-amber-900/30 border border-amber-800 rounded-md mb-4">
                <div className="flex items-start">
                  <AlertTriangle className="w-5 h-5 text-amber-500 mr-2 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-amber-200 text-sm font-medium mb-1">Warning: Moving backward in the status flow</p>
                    <p className="text-amber-200/80 text-sm">
                      This will remove the following timestamps:
                      <ul className="mt-1 ml-2 space-y-1">
                        {statusChangeDetails.affectedFields.map((field) => (
                          <li key={field}>• {field.replace('_at', '').replace('_', ' ')}</li>
                        ))}
                      </ul>
                    </p>
                  </div>
                </div>
              </div>
            )}

            {statusChangeDetails.newStatus === 'Cancelled' && (
              <div className="p-3 bg-red-900/30 border border-red-800 rounded-md mb-4">
                <div className="flex items-start">
                  <AlertTriangle className="w-5 h-5 text-red-500 mr-2 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-red-200 text-sm font-medium mb-1">Warning: This action cannot be undone</p>
                    <p className="text-red-200/80 text-sm">
                      Cancelling a truck will permanently mark it as cancelled. The truck will be moved to the history view
                      and will no longer appear in the regular scheduling views.
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div className="flex justify-end space-x-3">
              <button
                className="px-4 py-2 bg-slate-700 text-slate-300 rounded hover:bg-slate-600"
                onClick={cancelStatusChange}
              >
                Go Back
              </button>
              <button
                className={`px-4 py-2 ${statusChangeDetails.newStatus === 'Cancelled' ?
                  'bg-red-600 hover:bg-red-700' : 'bg-blue-600 hover:bg-blue-700'} text-white rounded`}
                onClick={confirmStatusChange}
              >
                {statusChangeDetails.newStatus === 'Cancelled' ? 'Cancel Truck' : 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Status Change Confirmation with BOL Upload UI */}
      {showStatusConfirmation && statusChangeDetails && (
        <StatusChangeConfirmation
          direction={statusChangeDetails.direction}
          fromStatus={statusChangeDetails.fromStatus}
          toStatus={statusChangeDetails.toStatus}
          onConfirm={async (customTimestamp, additionalData) => {
            console.log("Status change confirmed with additional data:", additionalData);

            // Special handling for BOL file uploads
            if (additionalData && additionalData.bolFile) {
              try {
                console.log("Handling BOL file upload");

                // Import the outbound truck service
                const outboundTruckService = await import('../outbound/services/outboundTruckService')
                  .then(module => module.default);

                // Use the special BOL upload endpoint directly
                const result = await outboundTruckService.markTruckAsCompletedWithBOL(
                  formData.id,
                  {
                    bolFile: additionalData.bolFile,
                    completed_at: customTimestamp ? customTimestamp.toISOString() : new Date().toISOString(),
                  }
                );

                console.log("BOL upload result:", result);

                // Close the editor after success
                onClose();
                return;
              } catch (error) {
                console.error("Error uploading BOL:", error);
              }
            }

            // Standard flow (if no BOL file or error with direct API call)
            let updatedData = {
              ...formData,
              status: statusChangeDetails.toStatus.key
            };

            // Add any additional data (excluding the file which needs special handling)
            if (additionalData) {
              const { bolFile, ...otherData } = additionalData;
              updatedData = {
                ...updatedData,
                ...otherData
              };
            }

            // Save the changes
            onSave(updatedData);

            // Close the confirmation dialog
            setShowStatusConfirmation(false);
            setStatusChangeDetails(null);
          }}
          onCancel={() => {
            setShowStatusConfirmation(false);
            setStatusChangeDetails(null);
          }}
          mode={mode}
        />
      )}
    </div>
  );
};

export default NewTruckEditor;